// ProjectManager.js
import React, { useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
  ListItemButton
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import {
  ChevronRight,
  Plus
} from 'lucide-react';
import { useTranslation } from 'react-i18next'; // Importar hook de traducción

import { ProjectContextProvider } from '../projects/ProjectContextProvider';
import ProjectsSection from '../projects/ProjectsSection';

// Nuevo contenedor principal para alinearse con CategoryManager
const ProjectManagerContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  boxSizing: 'border-box',
  padding: '0px',
}));

// Cambiado a ListItemButton para que sea clickeable por completo
const ProjectSectionHeader = styled(ListItemButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    margin: '4px 0',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? alpha('#FFFFFF', 0.05)
        : alpha(
          theme.palette.mode === 'dark' ? '#2C2C2E' : '#F2F2F7',
          0.8
        ),
    },
    cursor: 'pointer',
  };
});

const ProjectHeading = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontWeight: 600,
    fontSize: '16px',
    color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
  };
});

const AddProjectButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: '4px',
    color: isWallpaperMode ? '#FFFFFF' : theme.palette.primary.main,
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? alpha('#FFFFFF', 0.1)
        : 'rgba(3, 133, 255, 0.08)',
    },
  };
});

const CategoryChip = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    height: '20px',
    padding: '0 8px',
    borderRadius: '10px',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.1)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(142, 142, 147, 0.15)'
        : 'rgba(142, 142, 147, 0.1)'),
    color: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.8)'
      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)'),
    fontSize: '12px',
    fontWeight: 500,
    marginLeft: '8px',
    letterSpacing: '0.2px',
    textTransform: 'capitalize',
  };
});

const ProjectManager = forwardRef(({ 
  user,
  selectedCategory,
  selectedProject,
  onSelectProject,
  onProjectChatSelect,
  onModalOpenChange
}, ref) => {
  const theme = useTheme();
  const { t } = useTranslation(); // Inicializar hook de traducción
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(false);
  
  const projectsSectionRef = useRef(null);

  // Expone el método handleNewProject a través de la ref
  useImperativeHandle(ref, () => ({
    handleNewProject: () => {
      if (projectsSectionRef.current) {
        projectsSectionRef.current.handleNewProject();
      }
    }
  }));

  const toggleProjectsExpanded = useCallback(() => {
    setIsProjectsExpanded(prev => !prev);
  }, []);

  const handleProjectActionClick = useCallback((e) => {
    e.stopPropagation();
    if (projectsSectionRef.current) {
      projectsSectionRef.current.handleNewProject();
    }
  }, []);

  return (
    <ProjectManagerContainer>
      <ProjectSectionHeader onClick={toggleProjectsExpanded} disableRipple={false}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          flex: '1 1 auto', // Asegura que el título tome el espacio disponible
        }}>
          <ChevronRight
            size={20}
            style={{
              transform: isProjectsExpanded ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.25s ease-in-out',
              color: theme.palette.actualMode === 'wallpaper'
                ? '#FFFFFF'
                : theme.palette.text.secondary,
              marginRight: '8px',
            }}
          />
          <ProjectHeading>
            {t('projectManager.title')}
          </ProjectHeading>
          {selectedCategory && <CategoryChip>{selectedCategory}</CategoryChip>}
        </Box>
        
        <Box onClick={(e) => e.stopPropagation()}>
          <AddProjectButton onClick={handleProjectActionClick} size="small">
            <Tooltip title={t('projectManager.createTooltip')}>
              <Plus size={18} />
            </Tooltip>
          </AddProjectButton>
        </Box>
      </ProjectSectionHeader>

      <Collapse in={isProjectsExpanded} timeout="auto" sx={{ width: '100%' }}>
        <Box sx={{ px: 1.5 }}>
          <ProjectContextProvider>
            <ProjectsSection
              ref={projectsSectionRef}
              selectedCategory={selectedCategory}
              setSelectedProject={onSelectProject}
              selectedProject={selectedProject}
              user={user}
              onProjectChatSelect={onProjectChatSelect}
              onModalOpenChange={onModalOpenChange}
            />
          </ProjectContextProvider>
        </Box>
      </Collapse>
    </ProjectManagerContainer>
  );
});

export default ProjectManager;
